import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../lib/format";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tmApi, type TmProjectStatus, type TmProjectView, type TmTaskView } from "../../api/tm";
import { useAuth } from "../../auth/AuthContext";
import Ic from "../../components/icons.jsx";

type ProjectFilter = "all" | TmProjectStatus;

const STATUS_ORDER: TmProjectStatus[] = ["ACTIVE", "PLANNING", "ON_HOLD", "COMPLETED", "CANCELLED"];

/**
 * Projects landing — KPI strip (counts per status), a status-chip filter row,
 * and a richer card list that surfaces the caller's task load *in each
 * project* (open / overdue) so projects with attention-needed tasks float
 * naturally to the user's eye.
 *
 * Caller's tasks come from /tm/tasks/mine — that's the most personal lens.
 * Total project task counts would need a separate BE aggregate; deferred
 * until a screen actually needs it.
 */
export default function TmProjectsPage() {
  const { canWrite } = useSectionAccess("tm_projects");
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const [projects, setProjects] = useState<TmProjectView[] | null>(null);
  const [myTasks, setMyTasks] = useState<TmTaskView[]>([]);
  const [filter, setFilter] = useState<ProjectFilter>("all");
  const [search, setSearch] = useState("");

  const canCreate = canWrite && hasPermission("tm:manage_team");

  useEffect(() => {
    void tmApi.listProjects().then(setProjects).catch(() => setProjects([]));
    void tmApi.mine().then(setMyTasks).catch(() => setMyTasks([]));
  }, []);

  const today = useMemo(() => new Date(new Date().toDateString()), []);

  const tasksByProject = useMemo(() => {
    const m = new Map<string, { open: number; overdue: number }>();
    for (const task of myTasks) {
      if (!task.projectId) continue;
      const closed = task.status === "DONE" || task.status === "CANCELLED";
      const overdue =
        !closed
        && !!task.dueDate
        && new Date(task.dueDate) < today;
      const cur = m.get(task.projectId) ?? { open: 0, overdue: 0 };
      if (!closed) cur.open++;
      if (overdue) cur.overdue++;
      m.set(task.projectId, cur);
    }
    return m;
  }, [myTasks, today]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { ALL: 0 };
    (projects ?? []).forEach((p) => {
      c.ALL++;
      c[p.status] = (c[p.status] ?? 0) + 1;
    });
    return c;
  }, [projects]);

  const filtered = useMemo(() => {
    if (!projects) return [];
    const q = search.trim().toLowerCase();
    let out = projects.filter((p) => {
      if (filter !== "all" && p.status !== filter) return false;
      if (q) {
        const hay = `${p.name} ${p.ownerName} ${p.teamName ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    out = out.sort((a, b) => {
      // Sort: active first, then projects where I have overdue, then open tasks,
      // then alphabetical. Surfaces what needs attention now.
      const sa = STATUS_ORDER.indexOf(a.status);
      const sb = STATUS_ORDER.indexOf(b.status);
      if (sa !== sb) return sa - sb;
      const ta = tasksByProject.get(a.id) ?? { open: 0, overdue: 0 };
      const tb = tasksByProject.get(b.id) ?? { open: 0, overdue: 0 };
      if (tb.overdue !== ta.overdue) return tb.overdue - ta.overdue;
      if (tb.open !== ta.open) return tb.open - ta.open;
      return a.name.localeCompare(b.name);
    });
    return out;
  }, [projects, filter, search, tasksByProject]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("tm.projects.title", "Projects")}</h1>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
            {projects === null
              ? t("common.loading", "Loading…")
              : t("tm.projects.subtitle", {
                  defaultValue: "{{count}} project · {{active}} active",
                  defaultValue_other: "{{count}} projects · {{active}} active",
                  count: projects.length,
                  active: counts.ACTIVE ?? 0,
                })}
          </div>
        </div>
        {canCreate && (
          <Link to="/tm/projects/new" className="btn primary" style={{ textDecoration: "none" }}>
            <Ic.plus /> {t("tm.projects.new", "New project")}
          </Link>
        )}
      </div>

      <div style={kpiGrid}>
        <KpiTile label={t("tm.project.status.ACTIVE", "Active")}       value={counts.ACTIVE    ?? 0} accent="var(--ok)" bg="var(--ok-bg)" />
        <KpiTile label={t("tm.project.status.PLANNING", "Planning")}   value={counts.PLANNING  ?? 0} accent="var(--warn)" bg="var(--warn-bg)" />
        <KpiTile label={t("tm.project.status.ON_HOLD", "On hold")}     value={counts.ON_HOLD   ?? 0} accent="var(--ink-3)" bg="var(--surface-muted)" />
        <KpiTile label={t("tm.project.status.COMPLETED", "Completed")} value={counts.COMPLETED ?? 0} accent="var(--info)" bg="var(--info-bg)" />
      </div>

      <div style={chipBar}>
        <Chip label={t("tm.projects.filterAll", { defaultValue: "All {{count}}", count: counts.ALL ?? 0 })} active={filter === "all"} onClick={() => setFilter("all")} />
        {STATUS_ORDER.map((s) => {
          const n = counts[s] ?? 0;
          if (n === 0 && filter !== s) return null;
          return (
            <Chip
              key={s}
              label={`${t(`tm.project.status.${s}`, { defaultValue: statusLabel(s) })} ${n}`}
              active={filter === s}
              onClick={() => setFilter(filter === s ? "all" : s)}
            />
          );
        })}
      </div>

      <div style={searchWrap}>
        <span className="ord-search-icon" style={searchIcon}><Ic.search /></span>
        <input
          className="input"
          placeholder={t("tm.projects.searchPlaceholder", "Search project, owner, team…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ paddingLeft: 32, height: 36, width: "100%" }}
        />
      </div>

      {projects === null ? (
        <div style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>
      ) : filtered.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>📂</div>
          <b style={{ color: "var(--ink-2)" }}>
            {projects.length === 0
              ? t("tm.projects.empty", "No projects yet.")
              : t("tm.projects.emptyFiltered", "No projects match these filters.")}
          </b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {projects.length === 0
              ? t("tm.projects.emptyHint", "Once an admin creates a project you'll see it here.")
              : t("tm.projects.emptyFilteredHint", "Try clearing the filter or searching for a different name.")}
          </div>
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {filtered.map((p) => {
            const taskCounts = tasksByProject.get(p.id) ?? { open: 0, overdue: 0 };
            return (
              <li key={p.id}>
                <Link to={`/tm/projects/${p.id}`} style={{ textDecoration: "none", color: "inherit" }}>
                  <article
                    style={{
                      ...projectCard,
                      ...(taskCounts.overdue > 0 ? { boxShadow: "inset 3px 0 0 var(--err), var(--shadow-1)" } : {}),
                    }}
                  >
                    <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14, lineHeight: 1.3 }}>{p.name}</div>
                      <span style={statusPill(p.status)}>{t(`tm.project.status.${p.status}`, { defaultValue: statusLabel(p.status) })}</span>
                    </header>
                    <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                      {p.ownerName}{p.teamName ? ` · ${p.teamName}` : ""}
                    </div>
                    {p.targetEndDate && (
                      <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
                        {t("tm.projects.due", "Target")}: {formatDate(p.targetEndDate)}
                      </div>
                    )}
                    {(taskCounts.open > 0 || taskCounts.overdue > 0) && (
                      <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                        <span style={taskBadge("neutral")}>
                          {t("tm.projects.openForMe", { defaultValue: "{{count}} open for me", count: taskCounts.open })}
                        </span>
                        {taskCounts.overdue > 0 && (
                          <span style={taskBadge("danger")}>
                            {t("tm.projects.overdue", { defaultValue: "{{count}} overdue", count: taskCounts.overdue })}
                          </span>
                        )}
                      </div>
                    )}
                  </article>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function KpiTile({ label, value, accent, bg }: { label: string; value: number; accent: string; bg: string }) {
  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--border)",
      borderRadius: 10, padding: "12px 14px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: accent, boxShadow: `0 0 0 4px ${bg}` }} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? "var(--brand-primary)" : "var(--surface)",
        color: active ? "var(--on-primary)" : "var(--ink-2)",
        border: `1px solid ${active ? "var(--brand-primary)" : "var(--border)"}`,
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 12.5,
        cursor: "pointer",
        fontWeight: 500,
      }}
    >
      {label}
    </button>
  );
}

function statusPill(s: TmProjectStatus): React.CSSProperties {
  const palette = statusPalette(s);
  return {
    background: palette.bg, color: palette.fg,
    padding: "2px 8px", borderRadius: 999,
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3,
  };
}

function statusPalette(s: TmProjectStatus): { bg: string; fg: string } {
  switch (s) {
    case "ACTIVE":    return { bg: "var(--ok-bg)", fg: "var(--ok)" };
    case "PLANNING":  return { bg: "var(--warn-bg)", fg: "var(--warn)" };
    case "ON_HOLD":   return { bg: "var(--surface-muted)", fg: "var(--ink-3)" };
    case "COMPLETED": return { bg: "var(--info-bg)", fg: "var(--info)" };
    case "CANCELLED": return { bg: "var(--err-bg)", fg: "var(--err)" };
  }
}

function statusLabel(s: TmProjectStatus): string {
  switch (s) {
    case "ACTIVE":    return "Active";
    case "PLANNING":  return "Planning";
    case "ON_HOLD":   return "On hold";
    case "COMPLETED": return "Completed";
    case "CANCELLED": return "Cancelled";
  }
}

function taskBadge(kind: "neutral" | "danger"): React.CSSProperties {
  const palette = kind === "danger"
    ? { bg: "var(--err-bg)", fg: "var(--err)", border: "var(--err)" }
    : { bg: "var(--surface-muted)", fg: "var(--ink-2)", border: "var(--border)" };
  return {
    background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
    padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
  };
}

const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 };
const chipBar: React.CSSProperties = { display: "flex", gap: 8, flexWrap: "wrap" };
const searchWrap: React.CSSProperties = { position: "relative", display: "flex", alignItems: "center", maxWidth: 380 };
const searchIcon: React.CSSProperties = { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none" };
const projectCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 6, boxShadow: "var(--shadow-1)", transition: "transform .12s ease, box-shadow .12s ease" };
const emptyState: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
